// formik components
import { Form, Formik } from "formik";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// components
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

// example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// CompanyDetail layout schemas for form and form feilds
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CompanyInfo from "./info";
import validation, { validationCreateUser } from "./schemas/validations";
import initialValues, { initialValuesCreateUser } from "./schemas/initialValues";
import form, { createUserForm } from "./schemas/form";
import { CompanyService } from "services/companyService";
import { ImageService } from "services/imageService";
import toast from "react-hot-toast";
import Loading from "components/Common/Loading";
import { useEffect, useState } from "react";
import SoftTypography from "components/SoftTypography";
import CreateUser from "./components/create-user";
import { AuthService } from "services/authService";
import TableUser from "./components/table-user";
import { useSelector } from "react-redux";
import { authSelector } from "redux/selector";
import { ADMIN } from "constants/roles.constant";
import { UserService } from "services/userService";

function CompanyDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigation = useNavigate();
  const [data, setData] = useState();
  const { user } = useSelector(authSelector);
  const [refetch, setRefetch] = useState(false);
  const containEdit = location.pathname?.includes("/edit");

  const { formId, formField } = form;
  const { formId: formIdCreateUser, formField: formFieldCreateUser } = createUserForm;

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const { data: user } = await AuthService.getMe();
        let data = {};
        if (user.role === "Admin") {
          data = await CompanyService.getByIdAdmin(id);
        }
        if (user.role === "Super Admin") {
          data = await CompanyService.getById(id);
        }
        setData(data);
      } catch (error) {
        toast.error(error?.response?.data?.message || "Lấy dữ liệu thất bại");
      }
    })();
  }, [id]);

  const handleBack = () => navigation(-1);

  const submitForm = async (values, actions) => {
    try {
      const [url] = await ImageService.uploadMany([values.logo]);
      if (!url) return;
      const payload = {
        ...values,
        logo: url,
        subscriptions: "VIPs",
        accounting: [],
        industries: [],
      };
      if (id) {
        if (user.role === "Admin") {
          await CompanyService.adminUpdate(id, payload);
        }
        if (user.role === "Super Admin") {
          await CompanyService.update(id, payload);
        }
        await ImageService.deleteImage(data.logo);
      } else {
        await CompanyService.create(payload);
      }
      actions.setSubmitting(false);
      !id && actions.resetForm();
      toast.success(`${id ? "Cập nhật" : "Tạo"} thành công`);
      setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error) {
      toast.error(error?.response?.data?.message || `${id ? "Cập nhật" : "Tạo"} thất bại`);
      actions.setSubmitting(false);
      console.error({ error });
    }
  };

  const handleSubmit = (values, actions) => {
    submitForm(values, actions);
  };

  const handleSubmitCreateUser = async (values, actions) => {
    if (!id) return;
    try {
      console.log({ values });
      if (user.role === "Admin") {
        await UserService.createUserAdmin({ ...values, company: id });
      }
      if (user.role === "Super Admin") {
        await UserService.createUserSuperAdmin({ ...values, company: id });
      }
      toast.success("Thêm user thành công !");
      actions.setSubmitting(false);
      actions.resetForm();
      setRefetch((prev) => !prev);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Thêm user thất bại !");
      console.error({ error });
      actions.setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center" sx={{ height: "100%" }}>
          <Grid item xs={12} lg={12}>
            <Formik
              initialValues={initialValues}
              validationSchema={validation}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                <Form id={formId} autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <SoftBox p={2}>
                      <SoftBox>
                        <CompanyInfo
                          formData={{
                            values,
                            touched,
                            formField,
                            errors,
                            setFieldValue,
                          }}
                          data={data}
                        />
                        <SoftBox mt={10} width="100%" display="flex" justifyContent="space-between">
                          <SoftButton variant="gradient" color="light" onClick={handleBack}>
                            Trở về
                          </SoftButton>
                          {id && !containEdit && (
                            <SoftButton
                              variant="gradient"
                              color="dark"
                              href={`/manage-companies/companies/${id}/edit`}
                            >
                              Chỉnh sửa
                            </SoftButton>
                          )}
                          {(!id || (id && containEdit)) && (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                disabled={isSubmitting}
                                type="submit"
                                variant="gradient"
                                color="dark"
                              >
                                {isSubmitting ? (
                                  <Loading width={25} height={25} />
                                ) : id && containEdit ? (
                                  "Cập nhật"
                                ) : (
                                  "Tạo"
                                )}
                              </SoftButton>
                            </SoftBox>
                          )}
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
        {((user?.role !== ADMIN && id) || (user?.role === ADMIN && user?.company?._id)) && (
          <>
            <Grid container justifyContent="center" sx={{ height: "100%", mt: 4 }}>
              <Grid item xs={12} lg={12}>
                <Card>
                  <SoftBox p={2}>
                    <SoftBox lineHeight={0}>
                      <SoftTypography variant="h5" fontWeight="bold">
                        Thêm người dùng (user) cho công ty
                      </SoftTypography>
                    </SoftBox>
                    <Formik
                      initialValues={initialValuesCreateUser}
                      validationSchema={validationCreateUser}
                      onSubmit={handleSubmitCreateUser}
                    >
                      {({ values, errors, touched, isSubmitting: isSubmittingCreateUser }) => (
                        <Form id={formIdCreateUser} autoComplete="off">
                          <SoftBox>
                            <CreateUser
                              formData={{
                                values,
                                touched,
                                formField: formFieldCreateUser,
                                errors,
                              }}
                            />
                            <SoftBox
                              mt={4}
                              width="100%"
                              display="flex"
                              justifyContent="space-between"
                            >
                              {id && (
                                <SoftBox display="flex" gap={3}>
                                  <SoftButton
                                    disabled={isSubmittingCreateUser}
                                    type="submit"
                                    variant="gradient"
                                    color="dark"
                                  >
                                    {isSubmittingCreateUser ? (
                                      <Loading width={25} height={25} />
                                    ) : (
                                      "Thêm"
                                    )}
                                  </SoftButton>
                                </SoftBox>
                              )}
                            </SoftBox>
                          </SoftBox>
                        </Form>
                      )}
                    </Formik>
                  </SoftBox>
                </Card>
              </Grid>
            </Grid>
            <TableUser refetch={refetch} />
          </>
        )}
      </SoftBox>
    </DashboardLayout>
  );
}

export default CompanyDetail;
